import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Landmark, Plus, Edit2, Trash2, CheckCircle2, Star, Search, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import BankFormModal from './BankFormModal';
import { SingleChip, FilterButton, FiltersSheet } from './_filterChips';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BanksList = () => {
  const { token } = useAuth();
  const [banks, setBanks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [sheetOpen, setSheetOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/banks`, { headers }),
        axios.get(`${API}/properties`, { headers }),
      ]);
      setBanks(b.data.banks || []);
      setProperties(p.data || []);
    } catch (e) {
      toast({ title: 'Failed to load banks', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete "${b.bank_name} ${b.account_number_masked}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/banks/${b.id}`, { headers });
      toast({ title: 'Bank deleted' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const handleSetPrimary = async (b) => {
    try {
      await axios.post(`${API}/banks/${b.id}/set-primary`, {}, { headers });
      toast({ title: 'Marked as primary', description: `${b.bank_name} • ${b.account_number_masked}` });
      fetchAll();
    } catch (e) {
      toast({ title: 'Could not set primary', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const filtered = banks.filter(b => {
    if (propertyFilter === 'unlinked' && b.property_id) return false;
    if (propertyFilter !== 'all' && propertyFilter !== 'unlinked' && b.property_id !== propertyFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [b.bank_name, b.holder_name, b.ifsc, b.upi_vpa, b.account_number_masked, b.property_name].filter(Boolean).join(' ').toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const linked = banks.filter(b => b.property_id).length;
  const upiCount = banks.filter(b => b.upi_vpa).length;
  const primaries = banks.filter(b => b.is_primary_for_property).length;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="banks-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="text-emerald-600" /> Banks &amp; UPI
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Bank accounts and UPI VPAs used to receive rent. Mark one as primary per property.
          </p>
        </div>
        <Button
          data-testid="bank-add-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus size={16} className="mr-2" /> Add bank / UPI
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Total accounts" value={banks.length} Icon={Landmark} tint="emerald" />
        <KpiCard label="Linked to property" value={linked} Icon={CheckCircle2} tint="blue" />
        <KpiCard label="With UPI" value={upiCount} Icon={Smartphone} tint="indigo" />
        <KpiCard label="Primary set" value={primaries} Icon={Star} tint="amber" />
      </div>

      {/* Search + chips */}
      <Card className="p-4 border border-gray-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="bank-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bank, holder, IFSC, UPI…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          <SingleChip
            testId="chip-bank-property"
            label={
              propertyFilter === 'all' ? 'All Properties' :
              propertyFilter === 'unlinked' ? 'Unlinked only' :
              (properties.find(p => p.id === propertyFilter)?.name || 'Property')
            }
            options={[
              { key: 'all', label: 'All Properties' },
              { key: 'unlinked', label: 'Unlinked only' },
              ...properties.map(p => ({ key: p.id, label: p.name })),
            ]}
            value={propertyFilter}
            onChange={setPropertyFilter}
            highlight={propertyFilter !== 'all'}
          />
          <FilterButton
            testId="open-bank-filters"
            onClick={() => setSheetOpen(true)}
            count={propertyFilter !== 'all' ? 1 : 0}
          />
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="banks-empty">
          <Landmark size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No bank accounts yet</h3>
          <p className="text-sm text-gray-600 mb-4">
            Add your first bank account or UPI VPA — link it to a property to print on rent invoices.
          </p>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus size={16} className="mr-2" /> Add bank / UPI
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(b => (
            <BankCard
              key={b.id}
              bank={b}
              onEdit={() => { setEditing(b); setShowForm(true); }}
              onDelete={() => handleDelete(b)}
              onSetPrimary={() => handleSetPrimary(b)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <BankFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }}
          properties={properties}
          token={token}
          editing={editing}
        />
      )}

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Bank Filters"
        sections={[
          {
            key: 'propertyId', label: 'Property', kind: 'single',
            options: [
              { key: 'all', label: 'All Properties' },
              { key: 'unlinked', label: 'Unlinked only' },
              ...properties.map(p => ({ key: p.id, label: p.name })),
            ],
          },
        ]}
        values={{ propertyId: propertyFilter }}
        onApply={(d) => setPropertyFilter(d.propertyId)}
        onClear={() => { setPropertyFilter('all'); setSearch(''); }}
      />
    </div>
  );
};

const BankCard = ({ bank, onEdit, onDelete, onSetPrimary }) => (
  <Card data-testid={`bank-card-${bank.id}`} className="p-4 border border-gray-200 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Landmark size={16} className="text-emerald-600 shrink-0" />
          <h3 className="font-semibold text-gray-900 truncate">{bank.bank_name}</h3>
          {bank.is_primary_for_property && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              <Star size={10} /> PRIMARY
            </span>
          )}
          {bank.is_settlement_account && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
              SETTLEMENT
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-2">{bank.holder_name} • {bank.account_type}</p>
        <div className="space-y-1 text-sm">
          <p className="font-mono text-gray-900 tabular-nums">{bank.account_number_masked}</p>
          <p className="text-xs text-gray-600 font-mono">IFSC: {bank.ifsc}</p>
          {bank.upi_vpa && (
            <p className="text-xs text-indigo-700 font-mono inline-flex items-center gap-1">
              <Smartphone size={11} /> {bank.upi_vpa}
            </p>
          )}
        </div>
        {bank.property_name ? (
          <p className="text-xs text-gray-700 mt-2 inline-flex items-center gap-1">
            <CheckCircle2 size={11} className="text-emerald-600" /> {bank.property_name}
          </p>
        ) : (
          <p className="text-xs text-gray-400 italic mt-2">Not linked to any property</p>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        {!bank.is_primary_for_property && bank.property_id && (
          <button
            data-testid={`bank-primary-${bank.id}`}
            onClick={onSetPrimary}
            title="Set as primary for property"
            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
          >
            <Star size={14} />
          </button>
        )}
        <button
          data-testid={`bank-edit-${bank.id}`}
          onClick={onEdit}
          title="Edit"
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
        >
          <Edit2 size={14} />
        </button>
        <button
          data-testid={`bank-delete-${bank.id}`}
          onClick={onDelete}
          title="Delete"
          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  </Card>
);

const KpiCard = ({ label, value, Icon, tint }) => {
  const tints = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    blue:    'bg-blue-50 text-blue-700 border-blue-100',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
    amber:   'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <Card className={`p-4 border ${tints[tint]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
          <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
        </div>
        <Icon size={20} className="opacity-60" />
      </div>
    </Card>
  );
};

export default BanksList;
